import type { UnknownOutputParams } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { captureMessage } from '@/src/config/sentry'
import { CommonLayout } from '@/src/elements/templates/CommonLayout'
import { GradientTypography } from '@/src/elements/text/gradient'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { useFromWatchmanEmailStore } from '@/src/features/watchman/services/useAskWatchman'
import { WatchmanSection } from '@/src/features/watchman/ui/components/watchmanSection/WatchmanSection'
import { TEST_ID } from '@/src/general/constants/testId'
import { useSearchParamsAction } from '@/src/general/hooks/useSearchParamsAction'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useTranslate } from '@/src/lang/useTranslate'

import { IntentionFormButton } from './components/IntentionFormButton'
import { IntentionMarkAsRead } from './components/IntentionMarkAsRead'
import { IntentionNotificationMessages } from './components/IntentionNotificationMessages'
import { IntentionsList } from './components/IntentionsList'

type TIntentionsScreenProps = {
  searchParams: UnknownOutputParams
}

export const IntentionsScreen = ({ searchParams }: TIntentionsScreenProps): ReactNode => {
  const { t } = useTranslate()

  useSearchParamsAction({
    searchParams,
    action: () => {
      useFromWatchmanEmailStore.setState({ fromWatchmanEmail: true })
      if (!getIsLoggedIn()) captureMessage.then((f) => f('Unauthenticated watchman'))
    },
    requestedQuery: { isWatchman: '' },
  })

  const { initializeRef } = useAccessibilityContext()
  return (
    <CommonLayout testID={TEST_ID.INTENTIONS.SCREEN.NAME}>
      <View className="w-full max-w-[500px] gap-4 self-center px-4 xm:px-6">
        <AccessibilityHiddenTitle
          ref={(ref) => {
            initializeRef(ref, 'main')
          }}
        >
          <FormattedMessage id="app.accessibility.intentions" />
        </AccessibilityHiddenTitle>

        <GradientTypography
          classNameWrapper="native:h-20 native:my-10 web:flex"
          className="w-full items-center text-center text-xl font-black web:px-4 web:py-10 md:text-2xl"
          colors="primary"
          heading="h1"
        >
          {t('home.welcome.intentions.title')}
        </GradientTypography>

        <IntentionFormButton />

        {getIsLoggedIn() && (
          <>
            <IntentionMarkAsRead />
            <WatchmanSection trackingSource={TRACKING_SOURCE.INTENTIONS_PAGE_PRAY} theme={undefined} />
            <IntentionNotificationMessages />
          </>
        )}
        <IntentionsList trackingSource={TRACKING_SOURCE.INTENTIONS_PAGE_PRAY} />
      </View>
    </CommonLayout>
  )
}
